import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { postHowDidYouFind } from "../../api/auth/authServices";
import logoHalper from "../../assets/Logo.png";
import "./howDidYouFind.css";
import { trackEvent } from "../../analytics";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import {
  selectOnboardingTimeout,
  setOnboardingTimeout,
} from "../../redux/slices/authSlice/AuthSlice";

type BubbleOption = {
  id: string;
  icon: React.ReactNode;
  size: "lg" | "md" | "sm";
};

const ICON_SIZE = 22;

const InstagramIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient
        id="hdyfIgGrad"
        x1="2"
        y1="22"
        x2="22"
        y2="2"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#FFD600" />
        <stop offset="0.5" stopColor="#FF0169" />
        <stop offset="1" stopColor="#D300C5" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#hdyfIgGrad)" />
    <rect
      x="5.2"
      y="5.2"
      width="13.6"
      height="13.6"
      rx="3.8"
      stroke="#FFFFFF"
      strokeWidth="1.7"
    />
    <circle cx="12" cy="12" r="3.1" stroke="#FFFFFF" strokeWidth="1.7" />
    <circle cx="16.1" cy="7.9" r="0.95" fill="#FFFFFF" />
  </svg>
);

const YouTubeIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <path
      d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33z"
      fill="#FF0000"
    />
    <path d="M9.75 15.02l5.75-3.27-5.75-3.27v6.54z" fill="#FFFFFF" />
  </svg>
);

const ChatGPTIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <path
      d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.033.062L9.74 19.95a4.5 4.5 0 01-6.14-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"
      fill="#000000"
    />
  </svg>
);

const GoogleIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const AppStoreIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient
        id="hdyfAsGrad"
        x1="12"
        y1="2"
        x2="12"
        y2="22"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#00BFFC" />
        <stop offset="1" stopColor="#0066FF" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#hdyfAsGrad)" />
    <path
      d="M11.15 6.35a.98.98 0 011.7 0l.42.73-3.95 6.84H6.5a.95.95 0 110-1.9h1.72l2.93-5.07-.35-.6zM15.95 15.82h1.55a.95.95 0 100-1.9h-2.65l-1.98-3.43-1.1 1.9 3.08 5.33a.98.98 0 001.7-.98l-.6-1.02zM8.22 15.82l-.72 1.25a.98.98 0 01-1.7-.98l.95-1.65h5.2c.55 0 .95.38.95.95v.43H8.22z"
      fill="#FFFFFF"
    />
  </svg>
);

const AdsIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none">
    <path
      d="M18 4v13l-6.5-3.25H6A3 3 0 013 10.75v-.5a3 3 0 013-3h5.5L18 4z"
      fill="#FF6B00"
    />
    <path
      d="M6.5 13.75H9.5l1.3 4.2a1.2 1.2 0 01-1.15 1.55H8.2a1.2 1.2 0 01-1.16-.9l-.54-4.85z"
      fill="#FF9500"
    />
    <path
      d="M20.5 7.5a3.8 3.8 0 010 6"
      stroke="#FF6B00"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CheckMini = () => (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none">
    <path
      d="M20 6L9 17L4 12"
      stroke="#111827"
      strokeWidth={3.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LEFT_OPTIONS: BubbleOption[] = [
  { id: "instagram", icon: <InstagramIcon />, size: "lg" },
  { id: "youtube", icon: <YouTubeIcon />, size: "md" },
  { id: "chatgpt", icon: <ChatGPTIcon />, size: "md" },
];

const RIGHT_OPTIONS: BubbleOption[] = [
  { id: "google", icon: <GoogleIcon />, size: "sm" },
  { id: "app_store", icon: <AppStoreIcon />, size: "lg" },
  { id: "ads", icon: <AdsIcon />, size: "sm" },
];

export default function HowDidYouFind() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const onboardingTimeout = useAppSelector(selectOnboardingTimeout);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const selectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const userStatus = localStorage.getItem("user_status") || "individual";
    const findCode = userStatus === "salon" ? "238" : "240";
    const currentCode = localStorage.getItem("onboarding_code");

    if (currentCode !== "complete") {
      localStorage.setItem("onboarding_code", findCode);
    }

    sessionStorage.setItem("onboarding_force_route", "/how-did-you-find");
    sessionStorage.setItem("onboarding_force_status", userStatus);

    return () => {
      if (selectTimeoutRef.current) {
        clearTimeout(selectTimeoutRef.current);
      }
    };
  }, []);

  const goNext = useCallback(
    (selectedId: string) => {
      if (loading) return;
      if (selectTimeoutRef.current) {
        clearTimeout(selectTimeoutRef.current);
        selectTimeoutRef.current = null;
      }

      setLoading(true);

      postHowDidYouFind(selectedId)
        .then(() => {
          setLoading(false);
          const userStatus =
            localStorage.getItem("user_status") || "individual";
          localStorage.setItem("onboarding_completed", "true");
          localStorage.setItem("onboarding_code", "complete");
          sessionStorage.setItem(
            "onboarding_force_route",
            "/finish-registration",
          );
          sessionStorage.setItem("onboarding_force_status", userStatus);

          const startTime =
            onboardingTimeout ||
            sessionStorage.getItem("onboarding_timeout");

          const durationSeconds = startTime
            ? Math.max(
                0,
                Math.round(
                  (Date.now() - new Date(startTime).getTime()) / 1000,
                ),
              )
            : undefined;

          trackEvent("onboarding_completed", {
            duration_seconds: durationSeconds,
            source: selectedId,
            user_status: userStatus,
          });

          console.log("durationSeconds", durationSeconds);
          

          // Останавливаем таймер и очищаем его
          dispatch(setOnboardingTimeout(null));
          sessionStorage.removeItem("onboarding_timeout");

          navigate("/finish-registration", { replace: true });
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [loading, navigate],
  );

  const handleSelect = useCallback(
    (label: string) => {
      if (loading) return;
      setSelected(label);
      if (selectTimeoutRef.current) {
        clearTimeout(selectTimeoutRef.current);
      }
    },
    [goNext, loading],
  );

  const leftTopActive = selected === t("howDidYouFind.instagram");
  const leftMidActive = selected === t("howDidYouFind.youtube");
  const leftBotActive = selected === t("howDidYouFind.chatgpt");

  const rightTopActive = selected === t("howDidYouFind.google");
  const rightMidActive = selected === t("howDidYouFind.app_store");
  const rightBotActive = selected === t("howDidYouFind.ads");

  return (
    <div className="hdyf-wrapper">
      <motion.main
        className="hdyf-container"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        {/* Step progress indicator */}
        <div className="hdyf-step-crumb">{t("howDidYouFind.step")}</div>

        {/* Title */}
        <h1 className="hdyf-title">{t("howDidYouFind.title")}</h1>

        {/* Subtitle */}
        <p className="hdyf-subtitle">{t("howDidYouFind.subtitle")}</p>

        {/* Mindmap diagram */}
        <div className="hdyf-diagram">
          {/* Left Column (3 cards) */}
          <div className="hdyf-col hdyf-col-left">
            {LEFT_OPTIONS.map((opt) => {
              const label = t(`howDidYouFind.${opt.id}`);
              const isSelected = selected === label;

              return (
                <motion.button
                  key={opt.id}
                  type="button"
                  disabled={loading}
                  className={`hdyf-card-item ${
                    isSelected ? "hdyf-card-item--selected" : ""
                  }`}
                  onClick={() => handleSelect(label)}
                  whileHover={loading ? undefined : { scale: 1.02 }}
                  whileTap={loading ? undefined : { scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                >
                  <div className="hdyf-card-content">
                    <div className="hdyf-card-icon">{opt.icon}</div>
                    <span className="hdyf-card-label">{label}</span>
                  </div>

                  {/* Connector anchor dot */}
                  <span className="hdyf-card-connector-right" />

                  <AnimatePresence>
                    {isSelected && (
                      <motion.span
                        className="hdyf-check-badge"
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.3 }}
                      >
                        <CheckMini />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>

          {/* Left SVG Connectors */}
          <div className="hdyf-connector hdyf-connector-left">
            <svg
              className="hdyf-connector-svg"
              viewBox="0 0 90 252"
              fill="none"
              preserveAspectRatio="none"
            >
              {/* Top curve */}
              <path
                d="M 0 34 C 45 34, 45 126, 90 126"
                stroke={leftTopActive ? "#111827" : "#E2E8F0"}
                strokeWidth={leftTopActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
              {/* Mid horizontal */}
              <path
                d="M 0 126 L 90 126"
                stroke={leftMidActive ? "#111827" : "#E2E8F0"}
                strokeWidth={leftMidActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
              {/* Bot curve */}
              <path
                d="M 0 218 C 45 218, 45 126, 90 126"
                stroke={leftBotActive ? "#111827" : "#E2E8F0"}
                strokeWidth={leftBotActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
            </svg>
          </div>

          {/* Central Hub Node */}
          <div className="hdyf-center-node">
            <img src={logoHalper} alt="Halper" className="hdyf-center-logo" />
          </div>

          {/* Right SVG Connectors */}
          <div className="hdyf-connector hdyf-connector-right">
            <svg
              className="hdyf-connector-svg"
              viewBox="0 0 90 252"
              fill="none"
              preserveAspectRatio="none"
            >
              {/* Top curve */}
              <path
                d="M 0 126 C 45 126, 45 34, 90 34"
                stroke={rightTopActive ? "#111827" : "#E2E8F0"}
                strokeWidth={rightTopActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
              {/* Mid horizontal */}
              <path
                d="M 0 126 L 90 126"
                stroke={rightMidActive ? "#111827" : "#E2E8F0"}
                strokeWidth={rightMidActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
              {/* Bot curve */}
              <path
                d="M 0 126 C 45 126, 45 218, 90 218"
                stroke={rightBotActive ? "#111827" : "#E2E8F0"}
                strokeWidth={rightBotActive ? 2.2 : 1.5}
                strokeLinecap="round"
                style={{ transition: "stroke 0.22s, stroke-width 0.22s" }}
              />
            </svg>
          </div>

          {/* Right Column (3 cards) */}
          <div className="hdyf-col hdyf-col-right">
            {RIGHT_OPTIONS.map((opt) => {
              const label = t(`howDidYouFind.${opt.id}`);
              const isSelected = selected === label;

              return (
                <motion.button
                  key={opt.id}
                  type="button"
                  disabled={loading}
                  className={`hdyf-card-item ${
                    isSelected ? "hdyf-card-item--selected" : ""
                  }`}
                  onClick={() => handleSelect(label)}
                  whileHover={loading ? undefined : { scale: 1.02 }}
                  whileTap={loading ? undefined : { scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                >
                  {/* Connector anchor dot */}
                  <span className="hdyf-card-connector-left" />

                  <div className="hdyf-card-content">
                    <div className="hdyf-card-icon">{opt.icon}</div>
                    <span className="hdyf-card-label">{label}</span>
                  </div>

                  <AnimatePresence>
                    {isSelected && (
                      <motion.span
                        className="hdyf-check-badge"
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.3 }}
                      >
                        <CheckMini />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Bottom Actions matching screenshot */}
        <motion.div
          className="hdyf-actions"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <motion.button
            type="button"
            className="hdyf-continue-btn"
            disabled={selected === null || loading}
            onClick={() => goNext(selected)}
            whileHover={loading ? undefined : { scale: 1.02 }}
            whileTap={loading ? undefined : { scale: 0.98 }}
          >
            {loading ? t("loading") : t("continue")}
          </motion.button>

          <button
            type="button"
            className="hdyf-skip-link"
            disabled={loading}
            onClick={() => goNext(t("howDidYouFind.skipped"))}
          >
            {t("chat.skip")}
          </button>
        </motion.div>
      </motion.main>
    </div>
  );
}
